import { Checkbox, FormControlLabel, Typography, useTheme } from '@mui/material'
import ExternalLink from '@mui/material/Link'
import { useFlag } from '@unleash/proxy-client-react'
import { FirebaseError } from 'firebase/app'
import {
  UserCredential,
  getAuth,
  getRedirectResult,
  signInWithCredential,
  signInWithCustomToken,
  signInWithPopup,
  signInWithRedirect
} from 'firebase/auth'
import { isMobile } from 'mobile-device-detect'
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useEffectOnce } from 'react-use'
import { v4 } from 'uuid'
import GraphqlAlert from '../../components/GraphqlAlert'
import ViewTitle from '../../components/ViewTitle'
import PasswordVisibilityAdornment from '../../components/forms/PasswordVisibilityAdornment'
import StyledInputLabel from '../../components/forms/StyledInputLabel'
import StyledTextField from '../../components/forms/StyledTextField'
import { IdpFeatureFlagMap, LoginProvider, getLoginProviderConfig, supportedLoginProviderIds } from '../../config/firebase'
import AppleAuthProvider from '../../config/idps/apple'
import RequiredParameterException from '../../errors/RequiredParameter'
import logger from '../../utils/logger'
import PasswordValidator from '../../utils/passwordValidator'
import AuthFormFirebase from './components/AuthFormFirebase'
import InvitationHeader from './components/InvitationHeader'
import SSOButton, { IdpContainer } from './components/SSOButton'
import { StyledFormError, StyledFormInfo } from './components/StyledFormAlert'
import StyledFormButton from './components/StyledFormButton'
import StyledFormHeader from './components/StyledFormHeader'
import StyledFormHelperTypography from './components/StyledFormHelperTypography'
import StyledFormLegalDisclaimerTypography from './components/StyledFormLegalDisclaimerTypography'
import StyledFormLinkBold from './components/StyledFormLinkBold'
import useAppleLogin from './hooks/useAppleLogin'
import useGAEvent from './hooks/useGAEvent'
import useLoginFirebase from './hooks/useLogin'
import useRegisterCloudFunction from './hooks/useRegisterCloudFunction'

const REDIRECT_LOCAL_STORAGE_VAR = 'redirect_idp'

type Field = 'password' | 'confirmPassword' | 'email' | 'termsAndServices'

interface CallableError {
  errorInfo: {
    code: string
    message: string
  }
  codePrefix: string
}

interface Fields {
  email?: string
  password?: string
  confirmPassword?: string
  termsAndServices?: boolean
}

interface FieldErrors extends Fields {
  general?: string
  info?: string
}

const re = /\S+@\S+\.\S+/
const isValidEmail = (email: string) => {
  return re.test(email)
}

/**
 * Renders Registration Form
 */
export default function Register() {
  // styling & browser
  const theme = useTheme()
  const location = useLocation()
  const searchQueryRef = useRef(location.search)

  const { sendRegisterRequest } = useRegisterCloudFunction()

  // user input
  const [fields, setFields] = useState<Fields>({ termsAndServices: false })
  const [errors, setErrors] = useState<FieldErrors>({})

  // sso provider id
  const ssoProviderId = useRef<string>(localStorage.getItem(REDIRECT_LOCAL_STORAGE_VAR) || '')

  // used to block extra inputs from user
  const [isAttemptingSignUp, setIsAttemptingSignUp] = useState(false)

  // graphql
  const { handleAcceptTerms, results } = useLoginFirebase(true)
  const handleAcceptTermsRef = useRef(handleAcceptTerms)

  // Apple sign in
  const { init } = useAppleLogin()
  const initAppleRef = useRef(init)

  // individual idp flags
  const idpFeatureFlags: IdpFeatureFlagMap = {
    isFirebaseAppleIdpEnabled: useFlag('isFirebaseAppleIdpEnabled'),
    isFirebaseGoogleIdpEnabled: useFlag('isFirebaseGoogleIdpEnabled'),
    isFirebaseFacebookIdpEnabled: useFlag('isFirebaseFacebookIdpEnabled'),
    isFirebaseMicrosoftIdpEnabled: useFlag('isFirebaseMicrosoftIdpEnabled'),
    isFirebasePaypalIdpEnabled: useFlag('isFirebasePaypalIdpEnabled')
  }

  const enabledIdpCount = Object.values(idpFeatureFlags).filter((flag) => flag === true)

  // feature flag for sso provider
  // will evaluate to false if no idps are enabled
  const isFirebaseSsoEnabled = useFlag('isFirebaseSsoEnabled') && enabledIdpCount.length > 0

  const isButtonDisabled = () => isAttemptingSignUp || results.loading

  const [showPassword, setShowPassword] = useState(false)
  const handleClickShowPassword = () => setShowPassword(!showPassword)
  const { trackEvent } = useGAEvent()

  /**
   * Handles single click sign on for a {@link LoginProvider} (like Google, facebook, etc)
   *
   * @param provider a provide which extends Firebase's {@link BaseOAuthProvider}
   * @param attemptHelpDialogOnFail whether or not to open a helper dialog
   */
  const handleProvider = async (provider: LoginProvider) => {
    setIsAttemptingSignUp(true)

    if (!fields.termsAndServices) {
      const errorMsg = 'You must agree to our terms and services'
      setErrors({ general: errorMsg })
      setIsAttemptingSignUp(false)
      return
    }

    // custom flow for apple SAML login
    if (provider.providerId === AppleAuthProvider.PROVIDER_ID) {
      const response = await window.AppleID?.auth?.signIn().catch((e) => {
        logger.error('Error during Apple signIn', e)
        setIsAttemptingSignUp(false)
      })
      // signs in popup users
      if (response && response.authorization) {
        const appleCredential = new AppleAuthProvider().credential({ idToken: response.authorization.id_token })
        signInWithCredential(getAuth(), appleCredential)
        const authUser = getAuth()
        trackEvent('idp_register_success', {
          username: authUser.currentUser,
          termsAndServices: true,
          providerId: provider.providerId
        })
      }
      return
    }

    if (isMobile) {
      window.localStorage.setItem(REDIRECT_LOCAL_STORAGE_VAR, provider.providerId)
      signInWithRedirect(getAuth(), provider)
      const authUser = getAuth()
      trackEvent('idp_register_success', {
        username: authUser.currentUser,
        termsAndServices: true,
        providerId: provider.providerId
      })
      return
    }

    ssoProviderId.current = provider.providerId
    signInWithPopup(getAuth(), provider)
      .then(() => {
        const authUser = getAuth()
        trackEvent('idp_register_success', {
          username: authUser.currentUser,
          termsAndServices: true,
          providerId: provider.providerId
        })
      })
      .catch((err) => {
        if (err.code === 'auth/popup-blocked') {
          window.localStorage.setItem(REDIRECT_LOCAL_STORAGE_VAR, provider.providerId)
          signInWithRedirect(getAuth(), provider)
          return
        }
        setIsAttemptingSignUp(false)
      })
  }

  const validateFields = () => {
    const password = PasswordValidator(fields.password, fields.confirmPassword, fields.email)
    const { email } = fields

    if (!email) {
      throw new Error('Email required')
    }

    if (!isValidEmail(email)) {
      throw new RequiredParameterException('Email format is invalid.', 'email')
    }

    return { email, password }
  }

  // Handle form submission
  const register = async (event: FormEvent | undefined) => {
    event?.preventDefault()
    try {
      const { email, password } = validateFields()

      setIsAttemptingSignUp(true)

      const registerResult = await sendRegisterRequest(email, password)

      if ('error' in registerResult.data) {
        logger.error(registerResult.data.error)
        throw registerResult.data.error
      }

      const customToken = registerResult.data.firebaseToken
      await signInWithCustomToken(getAuth(), customToken)

      await handleAcceptTerms({
        optIntoMarketingEmails: {
          optedIn: fields.termsAndServices ?? false,
          updatedAt: new Date().toISOString()
        },
        termsAccepted: fields.termsAndServices ?? false
      })

      trackEvent('register_success', { username: getAuth().currentUser, email, termsAndServices: true })
    } catch (err) {
      let error = err as FirebaseError
      trackEvent('register_failed', { error })
      setIsAttemptingSignUp(false)

      // Unfortunately, in some cases firebase throws an error with a wrapper around it
      // typed as 'CallableError'.
      // In this scenario the error is nested in the `errorInfo` property.
      const callableError = err as CallableError
      if (callableError.errorInfo) {
        const { code, message } = callableError.errorInfo
        error = { name: code, code, message }
      }
      const paramError = error as RequiredParameterException

      switch (error.code) {
        case 'auth/email-already-in-use':
        case 'auth/username-already-taken':
        case 'auth/invalid-identifier':
        case 'auth/email-already-taken':
          setErrors({ info: 'email in use' })
          break
        case 'InvalidParameterException':
        case 'InvalidPasswordException':
          setFields({ ...fields, confirmPassword: '' })
          setErrors({ password: error.message })
          break
        case 'ConfirmPasswordException':
          setErrors({ confirmPassword: error.message, password: error.message })
          break
        case 'RequiredParameterException':
          setErrors({ [paramError.parameterName]: error.message })
          break
        default:
          setErrors({ general: error.message })
      }
    }
  }

  // Handle textfield change
  const handleChange = (event: ChangeEvent<HTMLInputElement>, checked?: boolean) => {
    const updatedFields = { ...fields }
    const field = event.target.name as Field
    if (field === 'termsAndServices') {
      updatedFields[field] = Boolean(checked)
    } else {
      updatedFields[field] = event.target.value
    }
    setFields(updatedFields)

    const updatedErrors = { ...errors }
    delete updatedErrors[field]
    delete updatedErrors.general
    setErrors(updatedErrors)
  }

  useEffectOnce(() => {
    trackEvent('register_view')
  })

  // apple sso sign up
  useEffect(() => {
    const params = new URLSearchParams(searchQueryRef.current)
    const attemptAppleRedirectSignIn = params.get('provider') === 'apple'
    const idToken = params.get('id_token')

    if (!idToken) {
      return
    }

    if (attemptAppleRedirectSignIn) {
      const credential = new AppleAuthProvider().credential({ idToken })
      signInWithCredential(getAuth(), credential)
        .then((res) => logger.info({ res }))
        .catch(logger.error)
    }
  }, [])

  // init apple login
  useEffect(() => {
    initAppleRef.current('register')
  }, [])

  // redirect login for mobile
  useEffect(() => {
    const redirectProviderId = localStorage.getItem(REDIRECT_LOCAL_STORAGE_VAR)

    if (redirectProviderId) {
      getRedirectResult(getAuth())
        .then(async (result: UserCredential | null) => {
          if (!result) {
            return
          }

          // terms of service include opt in to email agreement
          const isTermsAndServicesChecked = fields.termsAndServices ?? false

          handleAcceptTermsRef.current({
            optIntoMarketingEmails: {
              optedIn: isTermsAndServicesChecked,
              updatedAt: new Date().toISOString()
            },
            termsAccepted: isTermsAndServicesChecked
          })
        })
        .catch((error: FirebaseError) => {
          const provider = getLoginProviderConfig(redirectProviderId).getProvider()

          if (error.code === 'auth/account-exists-with-different-credential') {
            setErrors({
              info: 'email in use'
            })
          }

          logger.error({ error, provider })
        })
        .finally(() => localStorage.setItem(REDIRECT_LOCAL_STORAGE_VAR, ''))
    }
  }, [fields.termsAndServices])

  // Uses firebase auth listener to determine user has successfully signed in on firebase sdk,
  // then signs user into the graph api.
  useEffect(() => {
    const unsubscribe = getAuth().onAuthStateChanged(async (user) => {
      if (!user) {
        return
      }

      // Password flow needs custom handling due to firebase function implementation
      if (user.providerId === 'password') {
        return
      }

      // logs the user into our graph api once firebase recognizes a user
      try {
        // terms of service include opt in to email agreement
        const isTermsAndServicesChecked = fields.termsAndServices ?? false

        handleAcceptTermsRef.current({
          optIntoMarketingEmails: {
            optedIn: isTermsAndServicesChecked,
            updatedAt: new Date().toISOString()
          },
          termsAccepted: isTermsAndServicesChecked
        })
      } catch (err) {
        setIsAttemptingSignUp(false)
        const provider = getLoginProviderConfig(ssoProviderId.current).getProvider()
        const error = err as FirebaseError
        const uniqueErrorId = v4()
        logger.error({ uniqueErrorId, error })

        if (error.code === 'auth/popup-closed-by-user') {
          return
        }

        if (error.message.includes('account-exists-with-different-credential')) {
          const email: string = error.customData?.email as string
          if (email) {
            const formattedEmail = email ? ` (${email})` : ''
            const providerName = getLoginProviderConfig(provider.providerId).name
            setErrors({
              general: `An account exists with this email${formattedEmail}. You can enable single click sign on with ${providerName} under your account settings.`
            })
            return
          }
        }

        setErrors({ general: `There was an error signing in or registering your account. Unique error id: ${uniqueErrorId}` })
      } finally {
        localStorage.setItem(REDIRECT_LOCAL_STORAGE_VAR, '')
      }
    })

    return () => unsubscribe()
  }, [fields.termsAndServices])

  /**
   * @todo Remove BillHeader after new checkout is complete.
   */
  return (
    <AuthFormFirebase onSubmit={register}>
      <InvitationHeader />
      <ViewTitle title="Create an Account" />
      <GraphqlAlert error={results.error} />

      {/* Header & logo */}
      <StyledFormHeader>Create an Account</StyledFormHeader>

      {/* SSO Login buttons */}
      {isFirebaseSsoEnabled && (
        <IdpContainer>
          {supportedLoginProviderIds.map((providerId: string) => {
            const { featureFlagName, getProvider } = getLoginProviderConfig(providerId)
            const provider = getProvider()

            // hides this idp if not enabled in unleash
            if (idpFeatureFlags[featureFlagName] === false) {
              return null
            }

            return (
              <SSOButton
                key={`sso-button-${providerId}`}
                action="Sign Up with"
                disabled={isAttemptingSignUp}
                providerId={providerId}
                handleProvider={() => handleProvider(provider)}
              />
            )
          })}
        </IdpContainer>
      )}

      {/* Requires user to agree to terms and servies and privacy policy */}
      <FormControlLabel
        disableTypography
        sx={{
          paddingTop: 3,
          marginRight: 0,
          marginBottom: '20px',
          alignItems: 'flex-start',
          textAlign: 'left',
          '.MuiFormControlLabel-asterisk': {
            display: 'none'
          },
          '&.MuiFormControlLabel-root': {
            color: theme.palette.customGrey[600],
            flexGrow: 0,
            fontSize: '16px',
            lineHeight: '1.25'
          }
        }}
        control={
          <Checkbox
            id="termsAndServices"
            name="termsAndServices"
            required
            checked={Boolean(fields.termsAndServices)}
            aria-required
            onChange={handleChange}
            disabled={isAttemptingSignUp}
            data-test="terms-of-service-checkbox"
            inputProps={{
              'aria-label': 'Terms of Use and Privacy Policy checkbox'
            }}
            size="small"
            sx={{
              paddingTop: '1px',
              borderWidth: '1px'
            }}
          />
        }
        label={
          <Typography>
            You must agree to our{' '}
            <ExternalLink color="#1C47CB" data-test="terms-of-service" href="/legal/terms-of-service" target="_blank" underline="always">
              Terms of Use
            </ExternalLink>
            ,{' '}
            <ExternalLink color="#1C47CB" data-test="privacy-policy" href="/legal/privacy-policy" target="_blank" underline="always">
              Privacy Policy
            </ExternalLink>{' '}
            and{' '}
            <ExternalLink
              color="#1C47CB"
              data-test="biometric-information-privacy-policy"
              href="/legal/biometric-information-privacy-policy"
              target="_blank"
              underline="always"
            >
              Biometric Information Privacy Policy
            </ExternalLink>{' '}
            before continuing.
          </Typography>
        }
      />

      <StyledFormError title="Error logging in" body={errors.general} />
      {errors.info === 'email in use' && (
        <StyledFormInfo
          body={
            <Typography sx={{ color: '#1C47CB' }}>
              We found an account associated with this email address already. Please{' '}
              <strong>
                <Link style={{ color: '#1C47CB' }} color="#1C47CB" to={`/auth/login${location.search}`}>
                  sign in
                </Link>
              </strong>{' '}
              to continue selecting your plan.
            </Typography>
          }
        />
      )}

      <StyledInputLabel sx={{ mt: '18px' }} htmlFor="email">
        Email
      </StyledInputLabel>
      <StyledTextField
        id="email"
        autoFocus
        defaultValue=""
        data-test="email"
        disabled={isAttemptingSignUp}
        error={Boolean(errors.email)}
        fullWidth
        helperText={errors.email}
        inputProps={{ autoCapitalize: 'off' }}
        name="email"
        onChange={handleChange}
        required
        margin="dense"
        placeholder="name@gmail.com"
        type="email"
        variant="outlined"
      />

      <StyledInputLabel htmlFor="password">Password</StyledInputLabel>
      <StyledTextField
        id="password"
        data-test="password"
        disabled={isAttemptingSignUp}
        error={Boolean(errors.password)}
        fullWidth
        helperText={errors.password}
        margin="dense"
        name="password"
        data-sr-redact
        onChange={handleChange}
        required
        type={showPassword ? 'text' : 'password'}
        InputProps={{
          autoCapitalize: 'off',
          endAdornment: <PasswordVisibilityAdornment showPassword={showPassword} handleClickShowPassword={handleClickShowPassword} />
        }}
        placeholder=""
      />

      <StyledInputLabel htmlFor="confirmPassword">Confirm Password</StyledInputLabel>
      <StyledTextField
        id="confirmPassword"
        data-testid="confirmPassword"
        disabled={isAttemptingSignUp}
        error={Boolean(errors.confirmPassword)}
        fullWidth
        helperText={errors.confirmPassword}
        margin="dense"
        name="confirmPassword"
        onChange={handleChange}
        required
        type={showPassword ? 'text' : 'password'}
        placeholder=""
        InputProps={{
          autoCapitalize: 'off',
          endAdornment: <PasswordVisibilityAdornment showPassword={showPassword} handleClickShowPassword={handleClickShowPassword} />
        }}
      />

      <StyledFormButton color="primary" data-test="submit" disabled={isButtonDisabled()} fullWidth type="submit" variant="contained">
        Create Account
      </StyledFormButton>

      {/* Text below has been commented out until the free trial for sharing plan gets reinstated. Once that happens, text can be uncommented and updated. */}
      {/* <Typography component="p" sx={{ marginBottom: '10px' }}>
        If you are registering for a free trial, it requires a credit card and gives you 7 days free then $8USD per month plus applicable taxes until
        cancelled. Joining a group bucket as an invitee doesn't require payment.
      </Typography> */}

      {/* Google Captcha Disclaimer */}
      <StyledFormLegalDisclaimerTypography sx={{ fontSize: '16px' }}>
        This site is protected by reCAPTCHA and the Google{' '}
        <ExternalLink style={{ color: '#1C47CB' }} href="https://policies.google.com/privacy" underline="always" data-test="google-privacy">
          Privacy Policy
        </ExternalLink>{' '}
        and{' '}
        <ExternalLink style={{ color: '#1C47CB' }} href="https://policies.google.com/terms" underline="always" data-test="google-terms-of-service">
          Terms of Service
        </ExternalLink>{' '}
        apply.
      </StyledFormLegalDisclaimerTypography>

      <StyledFormHelperTypography>
        {`Already a member? `}
        <StyledFormLinkBold data-test="already-have-an-account-button" to={`/auth/login${location.search}`}>
          Sign In
        </StyledFormLinkBold>
      </StyledFormHelperTypography>
    </AuthFormFirebase>
  )
}
